import { InitialsAvatar } from '@alison/ui';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { GridRenderCellParams } from '@mui/x-data-grid';
import type { ReactNode } from 'react';
import { initialsFrom } from '../../../lib/format';
import { CreativeIdLine } from '../../creatives/CreativeIdLine';
import { NetworkBadge } from '../../creatives/NetworkBadge';
import { ThumbnailPreview } from '../../creatives/ThumbnailPreview';
import { ELLIPSIS_SX } from '../../creatives/utils';
import type { CompetitionCreativeRow } from './data/api';
import { competitionCreativeSubline, formatDateAdded, formatLength } from './utils';

const THUMB_SIZE = 66;
const COMPETITOR_AVATAR_SIZE = 28;

export interface CompetitionCreativeGridRow {
  id: string;
  creative: CompetitionCreativeRow;
  [key: string]: unknown;
}

export function toGridRow(creative: CompetitionCreativeRow): CompetitionCreativeGridRow {
  return { id: creative.creativeId, creative };
}

export function TaggedChip({ tagged }: { tagged: boolean }): ReactNode {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: 11,
        fontWeight: 700,
        py: 0.8,
        px: 1.6,
        borderRadius: '999px',
        bgcolor: tagged ? 'background.muted' : 'warning.bg',
        color: tagged ? 'text.secondary' : 'warning.dark',
      }}
    >
      {tagged ? 'TAGGED' : 'ELEMENTS NOT IDENTIFIED YET'}
    </Box>
  );
}

export function CreativeCell({ row }: GridRenderCellParams<CompetitionCreativeGridRow>): ReactNode {
  const { creative } = row;
  const subline = competitionCreativeSubline(creative);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0, height: '100%', pl: 2 }}>
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <ThumbnailPreview
          name={creative.creativeId}
          size={THUMB_SIZE}
          thumbnailUrl={creative.thumbnailUrl}
          previewUrl={creative.previewUrl}
          format={creative.format}
          duration={creative.duration}
        />
        <Box sx={{ position: 'absolute', top: 5, left: 5 }}>
          <NetworkBadge network={creative.network} padding={0.5} />
        </Box>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <CreativeIdLine creativeId={creative.creativeId} />
        <Tooltip title={subline} placement="bottom">
          <Typography color="text.secondary" sx={ELLIPSIS_SX}>
            {subline}
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );
}

export function CompetitorCell({
  row,
}: GridRenderCellParams<CompetitionCreativeGridRow>): ReactNode {
  const { competitorName, logoUrl } = row.creative;
  const initials = initialsFrom(competitorName);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, height: '100%' }}>
      {logoUrl === null && initials === null ? (
        <Box
          sx={{ width: COMPETITOR_AVATAR_SIZE, height: COMPETITOR_AVATAR_SIZE, flexShrink: 0 }}
        />
      ) : (
        <InitialsAvatar
          variant="rounded"
          size={COMPETITOR_AVATAR_SIZE}
          initials={initials ?? ''}
          src={logoUrl ?? undefined}
        />
      )}
      <Typography sx={ELLIPSIS_SX}>{competitorName ?? 'Unknown'}</Typography>
    </Box>
  );
}

function CenteredValueCell({ value }: { value: string }): ReactNode {
  return (
    <Typography
      variant="h5"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </Typography>
  );
}

export function DaysRunningCell({
  row,
}: GridRenderCellParams<CompetitionCreativeGridRow>): ReactNode {
  const { activeDays } = row.creative;
  return <CenteredValueCell value={activeDays === null ? '—' : String(activeDays)} />;
}

export function LengthCell({ row }: GridRenderCellParams<CompetitionCreativeGridRow>): ReactNode {
  return <CenteredValueCell value={formatLength(row.creative.duration)} />;
}

export function DateAddedCell({
  row,
}: GridRenderCellParams<CompetitionCreativeGridRow>): ReactNode {
  return <CenteredValueCell value={formatDateAdded(row.creative.firstSeenDate)} />;
}

export function TaggingCell({ row }: GridRenderCellParams<CompetitionCreativeGridRow>): ReactNode {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <TaggedChip tagged={row.creative.tagged} />
    </Box>
  );
}
